import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

const initialState = {
  feedbacks: [],
  currentFeedback: null,
  loading: false,
  error: null,
};

export const fetchFeedbacks = createAsyncThunk(
  'feedbacks/fetchFeedbacks',
  async () => {
    const response = await api.get('/feedbacks/feedbacks');
    return response.data;
  }
);

export const fetchFeedbackById = createAsyncThunk(
  'feedbacks/fetchFeedbackById',
  async (id) => {
    const response = await api.get(`/feedbacks/feedbacks/${id}`);
    return response.data;
  }
);

export const createFeedback = createAsyncThunk(
  'feedbacks/createFeedback',
  async (feedbackData) => {
    const response = await api.post('/feedbacks/feedbacks', feedbackData);
    return response.data;
  }
);

export const updateFeedback = createAsyncThunk(
  'feedbacks/updateFeedback',
  async ({ id, feedbackData }) => {
    const response = await api.put(`/feedbacks/feedbacks/${id}`, feedbackData);
    return response.data;
  }
);

export const deleteFeedback = createAsyncThunk(
  'feedbacks/deleteFeedback',
  async (id) => {
    await api.delete(`/feedbacks/feedbacks/${id}`);
    return id;
  }
);

const feedbackSlice = createSlice({
  name: 'feedbacks',
  initialState,
  reducers: {
    clearCurrentFeedback(state) {
      state.currentFeedback = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeedbacks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        state.feedbacks = action.payload;
      })
      .addCase(fetchFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch feedbacks';
      })
      .addCase(fetchFeedbackById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeedbackById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFeedback = action.payload;
      })
      .addCase(fetchFeedbackById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch feedback';
      })
      .addCase(createFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.feedbacks.push(action.payload);
      })
      .addCase(createFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create feedback';
      })
      .addCase(updateFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFeedback.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.feedbacks.findIndex((fb) => fb.id === action.payload.id);
        if (index !== -1) {
          state.feedbacks[index] = action.payload;
        }
        if (state.currentFeedback && state.currentFeedback.id === action.payload.id) {
          state.currentFeedback = action.payload;
        }
      })
      .addCase(updateFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update feedback';
      })
      .addCase(deleteFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.feedbacks = state.feedbacks.filter((fb) => fb.id !== action.payload);
        if (state.currentFeedback && state.currentFeedback.id === action.payload) {
          state.currentFeedback = null;
        }
      })
      .addCase(deleteFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete feedback';
      });
  },
});

export const { clearCurrentFeedback } = feedbackSlice.actions;

export default feedbackSlice.reducer;
